<template>
  <b-table
      :fields="fields"
      :items="items"
      table-class="text-nowrap"
      responsive
  >
    <template #cell(status)="data">
      <b-badge v-if="data.item.is_finished" variant="secondary">Finished</b-badge>
      <b-badge v-else-if="!!data.item.finishes_at" variant="warning">Stopped</b-badge>
      <b-badge v-else-if="data.item.last_paid && !!data.item.last_paid.date_failed" variant="danger">Payment Failed</b-badge>
      <b-badge v-else-if="data.item.last_paid" variant="success">Active</b-badge>
      <b-badge v-else variant="info">Trial</b-badge>
    </template>
    <template #cell(company)="data">
      <h4 class="mb-0">
        <b-link :to="{name: 'admin-company', params: {id: data.item.company.id}}">
          {{ data.item.company.name }}
        </b-link>
      </h4>
    </template>
    <template #cell(customer)="data">
      <b-link :to="{name: 'admin-user', params: {id: data.item.user.id}}">
        {{ data.item.user ? data.item.user.name : '' }}
      </b-link>
    </template>
    <template #cell(form_of_payment)="data">
      <template v-if="data.item.payment_method">
        <template v-if="data.item.payment_method.gateway_type === 'auth_net'">
          {{ data.item.payment_method.card_brand }} ending in {{data.item.payment_method.card_ending }}
          <div>Expires: {{ convertFromUtc(data.item.payment_method.card_expires, 'MM/YY') }}</div>
        </template>
        <template v-else-if="data.item.payment_method.gateway_type === 'nuvei'">
          ACH
        </template>
      </template>
    </template>
    <template #cell(pricing_plan)="data">
      <b-link :to="{name: 'admin-pricing-plan-view', params: {id: data.item.pricing_plan.id}}">
        {{ data.item.pricing_plan.name }}
      </b-link>
    </template>
    <template #cell(charge_amount)="data">
      {{ getFormattedPrice(data.item.payment_amount) }}
      <template v-if="data.item.pricing_plan.is_recurring_billing">
        ({{ getFormattedRecurringBilling(data.item.pricing_plan.billing_recurrence) }})
      </template>
    </template>
    <template #cell(last_charge)="data">
      <template v-if="data.item.last_paid">
        <template v-if="data.item.last_paid.date_failed">
          <div>{{ getFormattedPrice(data.item.payment_amount) }}</div>
          <span class="text-danger">failed on {{ convertFromUtc(data.item.last_paid.date_failed, 'L') }}</span>
          <div>
            <no-permission-wrapper :show="!canRetryBillingPayments">
              <b-btn
                  type="button"
                  variant="warning"
                  @click.prevent="() => retryPayment(data.item)"
                  :disabled="!canRetryBillingPayments"
              >
                Retry Payment
              </b-btn>
            </no-permission-wrapper>
          </div>
        </template>
        <template v-else>
          <div v-if="data.item.last_paid.amount">{{ getFormattedPrice(data.item.last_paid.amount) }}</div>
          <span class="text-success">paid on {{ convertFromUtc(data.item.last_paid.date, 'L') }}</span>
        </template>
      </template>
    </template>
    <template #cell(next_charge)="data">
      <template v-if="data.item.next_payment">
        {{ getFormattedPrice(data.item.next_payment.amount) }}
        <div>
          on {{ convertFromUtc(data.item.next_payment.date, 'L') }}
        </div>
<!--        <div>-->
<!--          <no-permission-wrapper :show="!canUpdateBilling">-->
<!--            <b-form-datepicker-->
<!--                v-model="nextChargeDate"-->
<!--                :min="todayDate"-->
<!--                button-only-->
<!--                button-variant="danger"-->
<!--                @shown="nextChargeDate = convertFromUtc(data.item.next_payment.date, 'YYYY-MM-DD')"-->
<!--                @input="(val) => changeChargeDate(data.item, val)"-->
<!--                locale="en-US"-->
<!--                :disabled="!canUpdateBilling"-->
<!--            >-->
<!--              <template v-slot:button-content="scope">-->
<!--                Change Date-->
<!--              </template>-->
<!--            </b-form-datepicker>-->
<!--          </no-permission-wrapper>-->
<!--        </div>-->
      </template>
    </template>
    <template #cell(start)="data">
      {{ convertFromUtc(data.item.starts_at, 'L') }}
    </template>
    <template #cell(end)="data">
      <template v-if="data.item.finishes_at">
        {{ convertFromUtc(data.item.finishes_at, 'L') }}
      </template>
    </template>
  </b-table>
</template>

<script>
import {convertFromUtc, getFormattedPrice, getFormattedRecurringBilling} from "../helpers";
import {API_ROUTES} from "../api";
import moment from "moment/moment";
import NoPermissionWrapper from "./NoPermissionWrapper.vue";

export default {
  name: "AdminBillingTable",
  components: {NoPermissionWrapper},
  emits: ['retry-payment', 'next-charge-change'],
  props: {
    billing: Array
  },
  data() {
    return {
      fields: [
        {
          key: "status",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "company",
          thClass: "border-top-0 admin-billing-company-col",
          tdClass: "align-middle admin-billing-company-col"
        },
        {
          key: "customer",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "form_of_payment",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "pricing_plan",
          thClass: "border-top-0 admin-billing-plan-col",
          tdClass: "align-middle admin-billing-plan-col"
        },
        {
          key: "charge_amount",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "last_charge",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "next_charge",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "start",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "end",
          thClass: "border-top-0",
          tdClass: "align-middle"
        }
      ],
      todayDate: moment().format('YYYY-MM-DD'),
      nextChargeDate: ""
    }
  },
  computed: {
    canUpdateBilling() {
      return this.$store.getters['auth/hasAdminPermissions']('billing_update');
    },
    canRetryBillingPayments() {
      return this.$store.getters['auth/hasAdminPermissions']('billing_retry');
    },
    items() {
      return this.billing;
    }
  },
  methods: {
    convertFromUtc,
    getFormattedRecurringBilling,
    getFormattedPrice,
    retryPayment(billingItem) {
      this.$bvModal.msgBoxConfirm(`You are going to retry a "${billingItem.pricing_plan.name}" pricing plan payment for ${billingItem.company.name}`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.$emit('retry-payment', {
            company: billingItem.company,
            pricing_plan: billingItem.pricing_plan
          })
        }
      });
    },
    changeChargeDate(billingItem, date) {
      const originalDate = this.convertFromUtc(billingItem.next_payment.date, 'L');
      const newDate = this.convertFromUtc(date, 'L');

      if (originalDate === newDate) {
        return;
      }

      this.$bvModal.msgBoxConfirm(`You are going to change a next charge date of "${billingItem.pricing_plan.name}" pricing plan payment for ${billingItem.company.name} from "${originalDate}" to "${newDate}"`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.$emit('next-charge-change', {
            company: billingItem.company,
            pricing_plan: billingItem.pricing_plan,
            date
          });
        }
      });
    }
  },
}
</script>
